import React from 'react';

/**
 * MatchReport — Exact replica of the JobastiIQ PDF report.
 * Clean white background, black text, blue logo. Hyphen bullets, no italic.
 */
const MatchReport = ({ data, candidateName, candidateInfo, collapsed, onToggle }) => {
  if (!data) return null;

  const cp = data.candidateProfile || {};
  const name = candidateName || cp.name || 'Candidate';
  const requirements = data.requirements || [];
  const mustHaves = requirements.filter(r => (r.importance || '').toLowerCase() === 'must-have');
  const niceToHaves = requirements.filter(r => (r.importance || '').toLowerCase() !== 'must-have');

  const oldMustHaves = data.must_have_scores || [];
  const oldNiceToHaves = data.nice_to_have_scores || [];
  const score = data.score ?? data.overall_score ?? 0;

  const renderEvidence = (evidence) => {
    if (!evidence || evidence === 'No evidence found') {
      return <div style={s.bullet}>- No evidence found</div>;
    }
    const bullets = evidence.includes(';')
      ? evidence.split(';').map(t => t.trim()).filter(Boolean)
      : [evidence];
    return bullets.map((b, i) => <div key={i} style={s.bullet}>- {b}</div>);
  };

  const renderRequirement = (req, idx) => (
    <div key={idx} style={{ marginBottom: 30 }}>
      <p style={s.reqTitle}>
        {req.name || req.requirement} – {req.score}%
      </p>
      <p style={s.reqExplanation}>
        {req.explanation || req.rationale || ''}
      </p>
      <p style={s.exampleLabel}>Example from CV:</p>
      <div style={s.bulletList}>
        {renderEvidence(req.evidence || req.cv_evidence)}
      </div>
    </div>
  );

  const hasMustHaves = mustHaves.length > 0 || oldMustHaves.length > 0;
  const hasNiceToHaves = niceToHaves.length > 0 || oldNiceToHaves.length > 0;

  return (
    <div style={s.page} data-testid="match-report">

      {/* Logo */}
      <div style={s.logoWrap}>
        <span style={s.logo}>JobastiIQ</span>
      </div>

      {/* Candidate Name */}
      <div style={s.nameWrap}>
        <h1 style={s.name} data-testid="match-report-candidate-name">{name}</h1>
      </div>

      {/* Candidate Info */}
      <div style={s.infoBlock}>
        {(candidateInfo?.noticePeriod || cp.availability) && (
          <div style={s.infoLine}>
            <span style={s.infoLabel}>Availability / Notice Period: </span>
            <span style={s.infoValue}>{candidateInfo?.noticePeriod || cp.availability}</span>
          </div>
        )}
        {(candidateInfo?.salaryExpectation || cp.salary_expectation) && (
          <div style={s.infoLine}>
            <span style={s.infoLabel}>Salary Expectation: </span>
            <span style={s.infoValue}>{candidateInfo?.salaryExpectation || cp.salary_expectation}</span>
          </div>
        )}
        {(candidateInfo?.locationCity || cp.location) && (
          <div style={s.infoLine}>
            <span style={s.infoLabel}>Location: </span>
            <span style={s.infoValue}>{candidateInfo?.locationCity || cp.location}</span>
          </div>
        )}
      </div>

      {/* Overall Match */}
      <div style={s.overallWrap}>
        <span style={s.overall} data-testid="match-report-score">Overall Match: ca. {score}%</span>
      </div>

      {/* Collapsible detail sections */}
      {!collapsed && (
        <>
          {/* Must-Have Requirements */}
          {hasMustHaves && (
            <div style={s.section}>
              <h2 style={s.sectionTitle}>Must-Have Requirements</h2>
              <hr style={s.divider} />
              <p style={s.subtitle}>Matching Against Requirements (with examples from CV)</p>

              {mustHaves.length > 0
                ? mustHaves.map(renderRequirement)
                : oldMustHaves.map((r, i) => renderRequirement({
                    name: r.requirement, score: r.score,
                    explanation: r.rationale, evidence: r.cv_evidence
                  }, i))
              }
            </div>
          )}

          {/* Nice-to-Have Requirements */}
          {hasNiceToHaves && (
            <div style={s.section}>
              <h2 style={s.sectionTitle}>Nice-to-Have Requirements</h2>
              <hr style={s.divider} />
              <p style={s.subtitle}>Matching Against Requirements (with examples from CV)</p>

              {niceToHaves.length > 0
                ? niceToHaves.map(renderRequirement)
                : oldNiceToHaves.map((r, i) => renderRequirement({
                    name: r.requirement, score: r.score,
                    explanation: r.rationale, evidence: r.cv_evidence
                  }, i))
              }
            </div>
          )}

          {/* Footer */}
          <div style={s.footer}>
            <p style={s.footerText}>JobastiIQ Consulting - {'\u201E'}Recruitment Redefined{'\u201C'}</p>
          </div>
        </>
      )}
    </div>
  );
};

/* ── Inline styles matching the PDF exactly ── */
const s = {
  page: {
    background: '#fff',
    maxWidth: 780,
    margin: '0 auto',
    padding: '48px 64px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#111',
    lineHeight: 1.55,
  },
  logoWrap: { textAlign: 'center', marginBottom: 36 },
  logo: { fontSize: 20, fontWeight: 700, color: '#2563eb', letterSpacing: '-0.3px' },
  nameWrap: { textAlign: 'center', marginBottom: 22 },
  name: { fontSize: 28, fontWeight: 700, margin: 0, color: '#111' },
  infoBlock: { textAlign: 'center', marginBottom: 10, lineHeight: 2 },
  infoLine: { fontSize: 13, color: '#333' },
  infoLabel: { fontWeight: 600, color: '#111' },
  infoValue: { color: '#333' },
  overallWrap: { textAlign: 'center', margin: '26px 0 36px 0' },
  overall: { fontSize: 16, fontWeight: 700, color: '#111' },
  section: { marginBottom: 40 },
  sectionTitle: { fontSize: 20, fontWeight: 700, color: '#111', margin: '0 0 6px 0' },
  divider: { border: 'none', borderTop: '1px solid #bbb', margin: '0 0 10px 0' },
  subtitle: { fontSize: 11, color: '#777', margin: '0 0 24px 0' },
  reqTitle: { fontWeight: 700, fontSize: 14, color: '#111', margin: '0 0 6px 0', lineHeight: 1.45 },
  reqExplanation: { fontSize: 12, color: '#333', margin: '0 0 14px 0', lineHeight: 1.65 },
  exampleLabel: { fontWeight: 700, fontSize: 12, color: '#111', margin: '0 0 4px 0' },
  bulletList: { paddingLeft: 16 },
  bullet: { fontSize: 12, color: '#333', lineHeight: 1.7, margin: '0 0 2px 0' },
  footer: { textAlign: 'center', borderTop: '1px solid #ccc', paddingTop: 24, marginTop: 48 },
  footerText: { fontSize: 11, color: '#999', margin: 0 },
};

export default MatchReport;
